import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useAuth } from './hooks/useAuth';
import { useProjects } from './hooks/useProjects';
import { usePSPSReport } from './hooks/usePSPSReport';
import { usePRDReport } from './hooks/usePRDReport';
import { useRealtimePSPS } from './hooks/useRealtimePSPS';
import { CT } from './lib/theme';
import { supabase } from './lib/supabase';
import AuthGate from './components/AuthGate';
import Sidebar, { type ViewMode } from './components/Sidebar';
import PSPSProject, { type FocusNodeFn } from './components/PSPSProject';
import PRDProject from './components/PRDProject';

const DEV_MODE_USERS = ['beliakov@cteleport.com'];

/**
 * Path URL format:
 *   /project/<id>                              — research mode (default)
 *   /project/<id>/research/<filters>           — research mode with filters
 *   /project/<id>/development                  — development mode
 *   /project/<id>/development/<filters>        — development mode with filters
 *   /project/<id>/<filters>                    — backward compat: research + filters
 */
export type HandleFilter = { nodeId: string; direction: 'in' | 'out' };

const VALID_MODES: ViewMode[] = ['research', 'development'];

function parseUrl(): { projectId: number | null; mode: ViewMode; filters: HandleFilter[] } {
  const path = window.location.pathname;
  const match = path.match(/^\/project\/(\d+)(?:\/(.+))?$/);
  if (!match) return { projectId: null, mode: 'research', filters: [] };

  const pid = Number(match[1]);
  const projectId = Number.isFinite(pid) && pid > 0 ? pid : null;
  let mode: ViewMode = 'research';
  let filterStr: string | undefined;

  if (match[2]) {
    // Check if first segment is a mode
    const slashIdx = match[2].indexOf('/');
    const firstSeg = slashIdx >= 0 ? match[2].slice(0, slashIdx) : match[2];

    if (VALID_MODES.includes(firstSeg as ViewMode)) {
      mode = firstSeg as ViewMode;
      filterStr = slashIdx >= 0 ? match[2].slice(slashIdx + 1) : undefined;
    } else {
      // Backward compat: no mode segment, treat as filters
      filterStr = match[2];
    }
  }

  const filters: HandleFilter[] = [];
  if (filterStr) {
    for (const s of filterStr.split(',')) {
      const fm = s.match(/^(.+)-(in|out)$/);
      if (fm) filters.push({ nodeId: fm[1], direction: fm[2] as 'in' | 'out' });
    }
  }

  return { projectId, mode, filters };
}

function buildUrl(projectId: number, mode: ViewMode, filters: HandleFilter[] = []): string {
  let url = `/project/${projectId}`;
  // Only include mode segment for non-default mode or when there are filters
  if (mode !== 'research' || filters.length > 0) {
    url += `/${mode}`;
  }
  if (filters.length > 0) {
    url += `/${filters.map((f) => `${f.nodeId}-${f.direction}`).join(',')}`;
  }
  return url;
}

function setUrlProjectId(id: number) {
  window.history.replaceState(null, '', `/project/${id}`);
}

export function getInitialFilter(): HandleFilter[] {
  return parseUrl().filters;
}

export function setUrlFilter(filters: HandleFilter[]) {
  const { projectId, mode } = parseUrl();
  if (projectId == null) return;
  window.history.replaceState(null, '', buildUrl(projectId, mode, filters));
}

function setUrlMode(projectId: number, mode: ViewMode) {
  window.history.replaceState(null, '', buildUrl(projectId, mode));
}

const PSPS_DOTS = [
  { letter: 'P', color: CT.orange },
  { letter: 'S', color: CT.purple },
  { letter: 'P', color: CT.blue },
  { letter: 'S', color: CT.green },
] as const;

function PSPSLoader({ message }: { message: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {PSPS_DOTS.map((d, i) => (
          <span
            key={i}
            style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              boxShadow: '0 0 0 2px #252A31',
              background: d.color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: CT.darkGrey,
              fontSize: 13,
              fontWeight: 600,
              fontFamily: CT.font,
              marginLeft: i === 0 ? 0 : -8,
              animation: `psps-bounce 1.4s ease-in-out ${i * 0.16}s infinite`,
            }}
          >
            {d.letter}
          </span>
        ))}
      </div>
      <div style={{ color: CT.paleGrey, fontSize: 13, fontFamily: CT.font }}>{message}</div>
    </div>
  );
}

export default function App() {
  const { isAuthenticated, organisationId, user, loading: authLoading } = useAuth();
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(
    () => parseUrl().projectId
  );
  const [mode, setMode] = useState<ViewMode>(() => parseUrl().mode);

  const showModeSwitcher = DEV_MODE_USERS.includes(user?.email ?? '');

  const handleModeChange = useCallback((newMode: ViewMode) => {
    setMode(newMode);
    if (selectedProjectId != null) {
      setUrlMode(selectedProjectId, newMode);
    }
  }, [selectedProjectId]);

  const selectProject = useCallback((id: number) => {
    setSelectedProjectId(id);
    setUrlProjectId(id);
    setMode('research'); // Reset to research when switching projects
  }, []);

  const { data: projects, isLoading: projectsLoading, error: projectsError } = useProjects(
    organisationId
  );
  const { data: pspsData, isLoading: reportLoading, error: reportError, refetch } =
    usePSPSReport(organisationId, selectedProjectId);

  // Derive hasSolutions and solution IDs from the already-loaded PSPS data
  const pspsSolutionIds = useMemo(
    () => pspsData?.nodes.filter((n) => n.data.type === 'solution').map((n) => n.data.id) ?? [],
    [pspsData],
  );
  const hasSolutions = pspsSolutionIds.length > 0;

  // Only fetch PRD graph when in development mode (solution IDs come from PSPS data)
  const {
    data: prdData,
    isLoading: prdLoading,
    error: prdError,
    refetch: prdRefetch,
  } = usePRDReport(
    showModeSwitcher && mode === 'development' ? organisationId : null,
    showModeSwitcher && mode === 'development' ? selectedProjectId : null,
    pspsSolutionIds,
  );

  useRealtimePSPS(selectedProjectId, mode === 'research' ? refetch : prdRefetch);

  // Update page title when project data loads
  useEffect(() => {
    if (mode === 'development' && prdData && selectedProjectId != null) {
      document.title = `${prdData.title} — PRD`;
    } else if (pspsData && selectedProjectId != null) {
      document.title = `${pspsData.title} — PSPS`;
    } else {
      document.title = 'PSPS Project';
    }
  }, [pspsData, prdData, selectedProjectId, mode]);

  const focusNodeRef = useRef<FocusNodeFn | null>(null);

  const [copied, setCopied] = useState(false);
  const copyShareLink = useCallback(() => {
    if (selectedProjectId == null) return;
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [selectedProjectId]);

  const handleFocusNode = useCallback((nodeId: string): boolean => {
    return focusNodeRef.current?.(nodeId) ?? false;
  }, []);

  if (authLoading) {
    return (
      <div
        style={{
          width: '100vw',
          height: '100vh',
          background: CT.darkGrey,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: CT.paleGrey,
          fontFamily: CT.font,
        }}
      >
        <PSPSLoader message="Signing in…" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <AuthGate />;
  }

  if (organisationId == null) {
    const emailDomain = user?.email?.split('@')[1] ?? 'unknown';
    return (
      <div
        style={{
          width: '100vw',
          height: '100vh',
          background: CT.darkGrey,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: CT.font,
          gap: 24,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {PSPS_DOTS.map((d, i) => (
            <span
              key={i}
              style={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                boxShadow: '0 0 0 2px #252A31',
                background: d.color,
                opacity: 0.4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: CT.darkGrey,
                fontSize: 15,
                fontWeight: 600,
                fontFamily: CT.font,
                marginLeft: i === 0 ? 0 : -8,
              }}
            >
              {d.letter}
            </span>
          ))}
        </div>
        <div style={{ textAlign: 'center', maxWidth: 400 }}>
          <div style={{ color: CT.white, fontSize: 17, fontWeight: 600, marginBottom: 10 }}>
            No organisation registered
          </div>
          <p style={{ color: CT.paleGrey, fontSize: 14, lineHeight: 1.6, margin: 0 }}>
            The domain <span style={{ color: CT.white, fontWeight: 500 }}>@{emailDomain}</span> is
            not associated with any organisation in PO Notebook.
          </p>
        </div>
        <button
          onClick={() => supabase.auth.signOut()}
          style={{
            background: 'transparent',
            color: CT.paleGrey,
            border: `1px solid ${CT.paleGrey}`,
            borderRadius: 8,
            padding: '10px 28px',
            fontSize: 14,
            fontWeight: 500,
            fontFamily: CT.font,
            cursor: 'pointer',
          }}
        >
          Sign out
        </button>
      </div>
    );
  }

  const selectedProjectName = projects.find((p) => p.id === selectedProjectId)?.name;

  return (
    <div
      style={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: CT.darkGrey,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Sidebar
        projects={projects}
        selectedId={selectedProjectId}
        onSelect={selectProject}
        isLoading={projectsLoading}
        error={projectsError}
        selectedProjectName={selectedProjectName}
        autoOpen={selectedProjectId == null}
        onCopyShareLink={copyShareLink}
        copied={copied}
        onFocusNode={handleFocusNode}
        mode={mode}
        onModeChange={handleModeChange}
        hasSolutions={hasSolutions}
        showModeSwitcher={showModeSwitcher}
      />

      <div style={{ flex: 1, position: 'relative', minHeight: 0 }}>
        {selectedProjectId == null ? (
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: CT.font,
              gap: 16,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, opacity: 0.3 }}>
              <span style={{ width: 18, height: 18, borderRadius: '50%', boxShadow: '0 0 0 2px #252A31', background: CT.orange }} />
              <span style={{ width: 32, height: 2, background: CT.paleGrey }} />
              <span style={{ width: 18, height: 18, borderRadius: '50%', boxShadow: '0 0 0 2px #252A31', background: CT.purple }} />
              <span style={{ width: 32, height: 2, background: CT.paleGrey }} />
              <span style={{ width: 18, height: 18, borderRadius: '50%', boxShadow: '0 0 0 2px #252A31', background: CT.blue }} />
              <span style={{ width: 32, height: 2, background: CT.paleGrey }} />
              <span style={{ width: 18, height: 18, borderRadius: '50%', boxShadow: '0 0 0 2px #252A31', background: CT.green }} />
            </div>
            <div style={{ color: CT.white, fontSize: 18, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ color: CT.orange }}>Persona</span>
              <span style={{ color: CT.paleGrey }}>→</span>
              <span style={{ color: CT.purple }}>Situation</span>
              <span style={{ color: CT.paleGrey }}>→</span>
              <span style={{ color: CT.blue }}>Pain</span>
              <span style={{ color: CT.paleGrey }}>→</span>
              <span style={{ color: CT.green }}>Solution</span>
            </div>
            <div style={{ color: CT.paleGrey, fontSize: 14, maxWidth: 360, textAlign: 'center', lineHeight: 1.5 }}>
              Select a project to explore its PSPS map. Click nodes to expand descriptions, click handles to trace connections.
            </div>
          </div>
        ) : mode === 'development' ? (
          // Development (PRD) mode
          prdError ? (
            <div
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#f87171',
                fontFamily: CT.font,
                fontSize: 14,
              }}
            >
              {prdError.message}
            </div>
          ) : prdLoading && !prdData ? (
            <div
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: CT.paleGrey,
                fontFamily: CT.font,
              }}
            >
              <PSPSLoader message="Loading PRD report…" />
            </div>
          ) : prdData && prdData.nodes.length > 0 ? (
            <PRDProject data={prdData} />
          ) : (
            // Zero screen for development mode
            <div
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: CT.font,
                gap: 16,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, opacity: 0.3 }}>
                <span style={{ width: 18, height: 18, borderRadius: '50%', boxShadow: '0 0 0 2px #252A31', background: CT.green }} />
                <span style={{ width: 32, height: 2, background: CT.paleGrey }} />
                <span style={{ width: 18, height: 18, borderRadius: '50%', boxShadow: '0 0 0 2px #252A31', background: CT.orange }} />
                <span style={{ width: 32, height: 2, background: CT.paleGrey }} />
                <span style={{ width: 18, height: 18, borderRadius: '50%', boxShadow: '0 0 0 2px #252A31', background: CT.primaryBlue }} />
              </div>
              <div style={{ color: CT.white, fontSize: 18, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ color: CT.green }}>Solution</span>
                <span style={{ color: CT.paleGrey }}>→</span>
                <span style={{ color: CT.orange }}>Deliverable</span>
                <span style={{ color: CT.paleGrey }}>→</span>
                <span style={{ color: CT.primaryBlue }}>PRD</span>
              </div>
              <div style={{ color: CT.paleGrey, fontSize: 14, maxWidth: 360, textAlign: 'center', lineHeight: 1.5 }}>
                No deliverables yet. Use Claude to organize solutions into deliverables and PRDs.
              </div>
            </div>
          )
        ) : (
          // Research (PSPS) mode
          reportError ? (
            <div
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#f87171',
                fontFamily: CT.font,
                fontSize: 14,
              }}
            >
              {reportError.message}
            </div>
          ) : reportLoading && !pspsData ? (
            <div
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: CT.paleGrey,
                fontFamily: CT.font,
              }}
            >
              <PSPSLoader message="Loading PSPS report…" />
            </div>
          ) : (
            <PSPSProject
              projectId={selectedProjectId}
              data={
                pspsData
                  ? { ...pspsData, nodes: pspsData.nodes, edges: pspsData.edges, minimal: true }
                  : null
              }
              onFocusNodeRef={focusNodeRef}
            />
          )
        )}
      </div>
    </div>
  );
}
